'use client'

import * as React from 'react'
import * as SwitchPrimitives from '@radix-ui/react-switch'
import { cx, cva, type VariantProps } from 'class-variance-authority'

const switchVariants = cva(
  'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=unchecked]:bg-slate-300',
  {
    variants: {
      variant: {
        default:
          'data-[state=checked]:bg-primary-500 focus-visible:ring-primary-500',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

type SwitchProps = React.ComponentPropsWithoutRef<
  typeof SwitchPrimitives.Root
> & { className?: string; variant?: string } & VariantProps<
    typeof switchVariants
  >
/**
 * Switch component
 * @param onCheckedChange - The function to call when the switch is checked or unchecked
 * @param checked - The state of the switch
 * @param className - The class name to apply to the switch
 * @param variant - The variant of the switch
 * @returns The Switch component
 */
export const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>((props, ref) => {
  const { className, variant, ...rest } = props
  return (
    <SwitchPrimitives.Root
      ref={ref}
      className={cx(switchVariants({ variant }), className)}
      {...rest}
    >
      <SwitchPrimitives.Thumb className='pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0' />
    </SwitchPrimitives.Root>
  )
})
Switch.displayName = SwitchPrimitives.Root.displayName
